/**
 * VP-175 — combobox z wyszukiwarką dla kroków wizarda (strona FB / konto reklamowe).
 *
 * Zamiast listy przycisków (nieczytelnej przy wielu stronach/kontach) pokazujemy
 * jeden dropdown: klik → lista z polem wyszukiwania (filtrowanie po wpisaniu).
 * Wzorzec Shadcn: Popover + Command (cmdk).
 */
import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "../ui/command";

export type ComboboxOption = {
  id: string;
  label: string;
  /** Dodatkowa informacja po prawej (np. waluta konta). */
  hint?: string;
};

type Props = {
  options: ComboboxOption[];
  value: string;
  onSelect: (id: string) => void;
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
};

export default function SearchableCombobox({
  options,
  value,
  onSelect,
  placeholder,
  searchPlaceholder,
  emptyText,
}: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className="w-full sm:w-80 flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-xs border border-white/12 bg-white/[0.04] text-left transition-colors hover:border-white/30"
        >
          <span className={selected ? "text-white truncate" : "text-white/40"}>
            {selected ? selected.label : placeholder}
            {selected?.hint ? <span className="text-white/40"> · {selected.hint}</span> : null}
          </span>
          <ChevronsUpDown className="w-3.5 h-3.5 text-white/40 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-64 p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem
                  key={o.id}
                  value={`${o.label} ${o.id}`}
                  onSelect={() => {
                    onSelect(o.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={`mr-2 h-3.5 w-3.5 shrink-0 ${o.id === value ? "opacity-100" : "opacity-0"}`}
                  />
                  <span className="truncate">{o.label}</span>
                  {o.hint ? (
                    <span className="ml-auto pl-2 text-xs text-muted-foreground">{o.hint}</span>
                  ) : null}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
